import { useEffect, useMemo, useState } from 'react';
import { getTelegramWebApp, applyTelegramThemeCssVars } from './lib/telegram';
import { apiGet, apiPost } from './lib/api';

type IconName =
  | 'crown'
  | 'lock'
  | 'flame'
  | 'book'
  | 'star'
  | 'brain'
  | 'alert'
  | 'chart'
  | 'add'
  | 'repeat'
  | 'listen'
  | 'arrow'
  | 'home'
  | 'settings'
  | 'chevron'
  | 'check'
  | 'volume'
  | 'globe'
  | 'gauge'
  | 'target'
  | 'message';

type HomeData = {
  todayWords: number;
  unlearnedWords: number;
  vocabCount: number;
  dailyQuizDone: string | null;
  mistakesCount: number;
  learnedWords: number;
  dueWords: number;
  timeZone: string;
  activity: {
    currentStreak: number;
    days: Array<{ date: string; active: boolean; isToday: boolean; isFuture: boolean }>;
    todayActivity: ActivityCounts;
    weekActivity: ActivityCounts;
  };
  plan: 'free' | 'premium' | 'complimentary';
  limits: {
    text_messages?: number | null;
    quizzes?: number | null;
    vocab_words?: number | null;
  } | null;
  usage: { text_messages?: number; quizzes?: number } | null;
  premiumPriceStars: number | null;
};

type ActivityCounts = { total: number; tutor: number; quiz: number; vocabulary: number; review: number };

function emptyActivityCounts(): ActivityCounts {
  return { total: 0, tutor: 0, quiz: 0, vocabulary: 0, review: 0 };
}

function localWeekDays() {
  const today = new Date();
  const monday = new Date(today);
  monday.setHours(12, 0, 0, 0);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return {
      date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`,
      active: false,
      isToday: date.toDateString() === today.toDateString(),
      isFuture: date > today,
    };
  });
}

function activityLabel(value: number) {
  return `${value} ${value === 1 ? 'activity' : 'activities'}`;
}

function streakLabel(value: number) {
  return `${value} day streak`;
}

function Icon({ name, className }: { name: IconName; className?: string }) {
  const content = {
    crown: <path d="m3 7 4.5 4L12 4l4.5 7L21 7l-2 11H5L3 7Zm3 8h12" />,
    lock: <><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
    flame: <path d="M13.5 2.5c.4 3-1.8 4.2-3.2 6.1-1.1 1.5-1.4 3.1-.4 4.8.4-1.7 1.5-2.6 2.6-3.6-.1 2.3 1.9 3.1 1.9 5.5a5.4 5.4 0 0 1-10.8 0c0-4.4 3.4-7.2 6.7-10.3.2 1.8.7 2.8 1.3 3.5.8-1.6 1.5-3.4 1.9-6Z" />,
    book: <><rect x="5" y="3" width="14" height="18" rx="2" /><path d="M8 7h8M8 11h8M8 15h5" /></>,
    star: <path d="m12 2.5 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3.1-5.8 3.1 1.1-6.5-4.7-4.6 6.5-.9L12 2.5Z" />,
    brain: <><path d="M9.5 4.5A3 3 0 0 0 4 6.2a3.1 3.1 0 0 0-1 5.8 3.1 3.1 0 0 0 2 5.5A3 3 0 0 0 10 20V4.5h-.5Z" /><path d="M14.5 4.5A3 3 0 0 1 20 6.2a3.1 3.1 0 0 1 1 5.8 3.1 3.1 0 0 1-2 5.5A3 3 0 0 1 14 20V4.5h.5ZM7 9.5h3M14 14.5h3" /></>,
    alert: <><circle cx="12" cy="12" r="9" /><path d="M12 7v6M12 17h.01" /></>,
    chart: <><path d="M5 20v-7M12 20V4M19 20v-11" /></>,
    add: <><path d="M12 5v14M5 12h14" /></>,
    repeat: <><path d="m17 2 4 4-4 4" /><path d="M3 11V9a3 3 0 0 1 3-3h15M7 22l-4-4 4-4" /><path d="M21 13v2a3 3 0 0 1-3 3H3" /></>,
    listen: <><path d="M4 14v-4a8 8 0 0 1 16 0v4" /><path d="M4 14a2 2 0 0 1 2-2h2v7H6a2 2 0 0 1-2-2v-3ZM20 14a2 2 0 0 0-2-2h-2v7h2a2 2 0 0 0 2-2v-3Z" /></>,
    arrow: <><path d="M5 12h13M14 8l4 4-4 4" /></>,
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v10h14V10M9 20v-6h6v6" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H3v-4h.1a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V3h4v.1a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" /></>,
    chevron: <path d="m9 18 6-6-6-6" />,
    check: <path d="m5 12 4 4L19 6" />,
    volume: <><path d="M11 5 6 9H3v6h3l5 4V5Z" /><path d="M15 9a4 4 0 0 1 0 6M18 6a8 8 0 0 1 0 12" /></>,
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></>,
    gauge: <><path d="M4 17a8 8 0 1 1 16 0" /><path d="m12 13 4-4" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
    message: <path d="M4 5h16v12H8l-4 4V5Z" />,
  }[name];

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {content}
    </svg>
  );
}

function pluralizeWords(value: number) {
  return `${value} ${value === 1 ? 'word' : 'words'}`;
}

function normalizeQuizStatus(value: string | null, done: number, limit: number | null) {
  if (value) return value.replace(/(\d+)\/(\d+)/, '$1 / $2');
  if (limit == null) return 'Unlimited';
  return `${done} / ${limit} done`;
}

type ViewName = 'home' | 'vocabulary' | 'quiz' | 'studyPlan' | 'settings' | 'voice' | 'speed' | 'level' | 'native' | 'accent' | 'goal' | 'reply';
type UserSettings = {
  voice: string;
  tts_voice_id?: string | null;
  speed: string;
  level: string;
  audio: boolean;
  native_lang: string;
  accent: string;
  voice_reply_mode: string;
  goal: string;
};
type QuizState = {
  sessionId: string;
  title: string;
  index: number;
  total: number;
  correct: number;
  question: { prompt: string; choices: string[] };
};
type StudyPlanTopic = {
  topic: string;
  label: string;
  status: 'not_started' | 'needs_practice' | 'developing' | 'mastered';
  score: number;
  attempts: number;
  successes: number;
  mistakes: number;
};
type StudyPlanData = {
  topics: StudyPlanTopic[];
  focus: string[];
  summary: { total: number; practiced: number; needsPractice: number; developing: number; mastered: number };
};

const studyPlanPreview: StudyPlanData = (() => {
  const labels = [
    'Present Simple', 'Present Continuous', 'Past Simple', 'Past Continuous',
    'Present Perfect', 'Future Forms', 'Articles', 'Prepositions',
    'Subject–Verb Agreement', 'Word Order', 'Modal Verbs', 'Conditionals',
    'Comparatives', 'Pronouns', 'Countable Nouns', 'Gerunds & Infinitives', 'Vocabulary Choice',
  ];
  const topics = labels.map((label, index): StudyPlanTopic => {
    const status: StudyPlanTopic['status'] = index < 2 ? 'mastered' : index < 5 ? 'developing' : index < 8 ? 'needs_practice' : 'not_started';
    const attempts = status === 'not_started' ? 0 : 8 - Math.min(index, 5);
    const mistakes = status === 'mastered' ? 0 : status === 'developing' ? 2 : status === 'needs_practice' ? 4 : 0;
    return {
      topic: label.toLowerCase().replace(/[^a-z]+/g, '_').replace(/^_|_$/g, ''), label, status,
      score: status === 'mastered' ? 92 - index * 3 : status === 'developing' ? 68 - index * 3 : status === 'needs_practice' ? 38 - index : 0,
      attempts, successes: Math.max(0, attempts - mistakes), mistakes,
    };
  });
  return { topics, focus: topics.slice(5, 8).map(({ topic }) => topic), summary: { total: 17, practiced: 8, needsPractice: 3, developing: 3, mastered: 2 } };
})();

const settingDefaults: UserSettings = {
  voice: 'shimmer', speed: 'normal', level: 'B1', audio: true,
  native_lang: 'en', accent: 'us', voice_reply_mode: 'voice_only', goal: 'everyday',
};
const languageNames: Record<string, string> = { en: 'English', ru: 'Russian', uk: 'Ukrainian', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian', pt: 'Portuguese', tr: 'Turkish', ka: 'Georgian' };

function ScreenHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return <header className="screenHeader"><button type="button" onClick={onBack} aria-label="Back">‹</button><h1>{title}</h1><span /></header>;
}

function BottomNav({ view, onChange }: { view: ViewName; onChange: (view: ViewName) => void }) {
  const items: Array<[ViewName, IconName, string]> = [['home', 'home', 'Home'], ['vocabulary', 'book', 'Vocab'], ['quiz', 'brain', 'Quiz'], ['settings', 'settings', 'Settings']];
  return <nav className="bottomNav">{items.map(([value, icon, label]) => <button className={view === value ? 'active' : ''} type="button" key={value} onClick={() => onChange(value)}><Icon name={icon} /><span>{label}</span></button>)}</nav>;
}

function VocabularyView({ onBack }: { onBack: () => void }) {
  const [filter, setFilter] = useState<'all' | 'learned' | 'unlearned'>('all');
  const [words, setWords] = useState<Array<{ word: string; definition: string; learned: boolean; errors: number }>>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet<any>(`/api/miniapp/vocabulary?filter=${filter}`).then((data) => {
      if (!cancelled) setWords(Array.isArray(data?.words) ? data.words : []);
    }).catch(() => {
      if (!cancelled) setWords((import.meta as any).env?.DEV ? [
        { word: 'deadline', definition: 'the latest time something must be finished', learned: true, errors: 1 },
        { word: 'achieve', definition: 'to successfully reach a goal', learned: true, errors: 0 },
        { word: 'confident', definition: 'feeling sure about your abilities', learned: false, errors: 2 },
        { word: 'improve', definition: 'to make or become better', learned: false, errors: 0 },
      ].filter((item) => filter === 'all' || (filter === 'learned' ? item.learned : !item.learned)) : []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter]);
  return <section className="screenPage"><ScreenHeader title="My Vocabulary" onBack={onBack} /><div className="filterTabs">{(['all', 'learned', 'unlearned'] as const).map((value) => <button type="button" className={filter === value ? 'active' : ''} onClick={() => setFilter(value)} key={value}>{value[0].toUpperCase() + value.slice(1)}</button>)}</div><div className="wordList">{loading ? <p className="emptyState">Loading…</p> : words.length ? words.map((item) => <article className="wordRow" key={item.word}><span className={`wordStatus ${item.learned ? 'learned' : ''}`}><Icon name={item.learned ? 'check' : 'book'} /></span><span className="wordCopy"><strong>{item.word}</strong><small>{item.definition}</small></span>{item.errors > 0 && <span className="errorBadge">{item.errors} err</span>}</article>) : <p className="emptyState">No words in this list yet.</p>}</div></section>;
}

function QuizView({ onBack, tg }: { onBack: () => void; tg: ReturnType<typeof getTelegramWebApp> }) {
  const [quiz, setQuiz] = useState<QuizState | null>(() => (import.meta as any).env?.DEV ? {
    sessionId: 'preview', title: 'Daily Quiz', index: 0, total: 5, correct: 0,
    question: { prompt: 'What does “deadline” mean?', choices: ['A meeting at work', 'The latest time to finish something', 'A short break', 'A new goal'] },
  } : null);
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(false);
  async function start() {
    setBusy(true); setCompleted(false);
    try { const data = await apiPost<any>('/api/miniapp/quiz/start'); setQuiz({ ...data, correct: 0 }); }
    catch { tg?.showAlert?.('Could not start the quiz. Check your daily limit and try again.'); }
    finally { setBusy(false); }
  }
  async function answer(choice: string) {
    if (!quiz || busy) return;
    setBusy(true);
    try {
      const data = await apiPost<any>('/api/miniapp/quiz/answer', { sessionId: quiz.sessionId, answer: choice });
      const correct = quiz.correct + (data.wasCorrect ? 1 : 0);
      if (data.completed) { setQuiz({ ...quiz, correct }); setCompleted(true); }
      else setQuiz({ ...data, correct });
    } catch { tg?.showAlert?.('Could not save the answer. Please try again.'); }
    finally { setBusy(false); }
  }
  return <section className="screenPage quizPage"><ScreenHeader title="Daily Quiz" onBack={onBack} />{!quiz || completed ? <div className="quizEmpty"><span className="bigRoundIcon"><Icon name="brain" /></span><h2>{completed ? 'Quiz complete!' : 'Ready for today’s quiz?'}</h2><p>{completed ? `${quiz?.correct || 0} correct answers` : 'Five personalized English questions.'}</p><button type="button" onClick={start} disabled={busy}>{busy ? 'Preparing…' : completed ? 'Practice again' : 'Start Quiz'}</button></div> : <><div className="quizMeta"><span>Question {quiz.index + 1} of {quiz.total}</span><strong>{quiz.correct} correct</strong></div><div className="quizTrack"><span style={{ width: `${(quiz.index / quiz.total) * 100}%` }} /></div><article className="questionCard"><small>WHAT DOES THIS MEAN?</small><h2>{quiz.question.prompt}</h2></article><div className="answerList">{quiz.question.choices.map((choice, index) => <button type="button" key={`${choice}-${index}`} onClick={() => answer(choice)} disabled={busy}><span>{String.fromCharCode(65 + index)}</span>{choice}</button>)}</div></>}</section>;
}

function StudyPlanView({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<StudyPlanData | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    apiGet<any>('/api/miniapp/study-plan').then((result) => {
      if (!cancelled && result?.ok) setData({ topics: result.topics || [], focus: result.focus || [], summary: result.summary });
      else if (!cancelled && (import.meta as any).env?.DEV) setData(studyPlanPreview);
    }).catch(() => { if (!cancelled && (import.meta as any).env?.DEV) setData(studyPlanPreview); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const focusTopics = data?.focus.map((topic) => data.topics.find((item) => item.topic === topic)).filter(Boolean) as StudyPlanTopic[] | undefined;
  const statusLabel = (status: StudyPlanTopic['status']) => ({ not_started: 'Not started', needs_practice: 'Needs practice', developing: 'Developing', mastered: 'Mastered' })[status];
  return <section className="screenPage studyPlanPage"><ScreenHeader title="Study Plan" onBack={onBack} />{loading ? <p className="emptyState">Loading your plan…</p> : !data ? <p className="emptyState">Could not load your study plan.</p> : <><section className="planSummary"><div><strong>{data.summary.mastered}</strong><span>Mastered</span></div><div><strong>{data.summary.developing}</strong><span>Developing</span></div><div><strong>{data.summary.needsPractice}</strong><span>Practice</span></div></section><section className="planOverview"><div className="planOverviewTop"><span>Topics explored</span><strong>{data.summary.practiced} / {data.summary.total}</strong></div><span className="planOverviewTrack"><span style={{ width: `${(data.summary.practiced / Math.max(1, data.summary.total)) * 100}%` }} /></span></section>{focusTopics?.length ? <section className="focusSection"><h2>FOCUS NOW</h2><div>{focusTopics.map((topic) => <span key={topic.topic}>{topic.label}</span>)}</div></section> : <section className="planEmptyPrompt"><Icon name="target" /><div><strong>Start practising</strong><small>Your priorities will appear after a few text or voice messages.</small></div></section>}<h2 className="planTopicsTitle">ALL TOPICS</h2><div className="topicList">{data.topics.map((topic) => <article className={`topicRow topicRow--${topic.status}`} key={topic.topic}><span className="topicIcon">{topic.status === 'mastered' ? <Icon name="check" /> : <Icon name="book" />}</span><span className="topicCopy"><strong>{topic.label}</strong><small>{topic.attempts ? `${topic.successes} successful uses · ${topic.attempts} observations` : 'No evidence yet'}</small><span className="topicTrack"><span style={{ width: `${topic.score}%` }} /></span></span><span className="topicStatus">{statusLabel(topic.status)}</span></article>)}</div></>}</section>;
}

const pickerDefinitions: Record<string, Array<[string, string, string?]>> = {
  voice: [['alloy', 'Alloy'], ['ash', 'Ash'], ['coral', 'Coral'], ['echo', 'Echo'], ['fable', 'Fable'], ['onyx', 'Onyx'], ['nova', 'Nova'], ['shimmer', 'Shimmer'], ['off', 'Voice Off']],
  speed: [['very_slow', 'Very Slow'], ['slow', 'Slow'], ['normal', 'Normal'], ['native', 'Native']],
  level: [['A0', 'A0', 'Starter'], ['A1', 'A1', 'Beginner'], ['A2', 'A2', 'Elementary'], ['B1', 'B1', 'Intermediate'], ['B2', 'B2', 'Upper intermediate'], ['C1', 'C1', 'Advanced'], ['C2', 'C2', 'Proficient']],
  native: Object.entries(languageNames).map(([value, label]) => [value, label]),
  accent: [['us', 'American English', 'US'], ['uk', 'British English', 'UK']],
  goal: [['everyday', 'Everyday English'], ['travel', 'Travel'], ['business', 'Business'], ['ielts', 'IELTS']],
  reply: [['voice_only', 'Voice after voice messages'], ['always', 'Always reply with voice']],
};
const pickerFields: Record<string, keyof UserSettings> = { voice: 'voice', speed: 'speed', level: 'level', native: 'native_lang', accent: 'accent', goal: 'goal', reply: 'voice_reply_mode' };
const pickerTitles: Record<string, string> = { voice: 'Choose Voice', speed: 'Voice Speed', level: 'English Level', native: 'Native Language', accent: 'English Accent', goal: 'Learning Goal', reply: 'Voice Replies' };

function PickerView({ kind, settings, onSave, onBack }: { kind: string; settings: UserSettings; onSave: (patch: Partial<UserSettings>) => Promise<void>; onBack: () => void }) {
  const field = pickerFields[kind];
  const current = String(settings[field]);
  const [elevenVoices, setElevenVoices] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    if (kind !== 'voice') return;
    apiGet<any>('/api/miniapp/voices').then((data) => setElevenVoices(Array.isArray(data?.voices) ? data.voices : [])).catch(() => {});
  }, [kind]);
  const options = kind === 'voice' && elevenVoices.length
    ? [...elevenVoices.map((voice) => [voice.id, voice.name, 'ElevenLabs'] as [string, string, string]), ['off', 'Voice Off'] as [string, string]]
    : pickerDefinitions[kind];
  const selectedValue = kind === 'voice' && settings.tts_voice_id ? settings.tts_voice_id : current;
  return <section className="screenPage"><ScreenHeader title={pickerTitles[kind]} onBack={onBack} /><div className={`pickerGrid pickerGrid--${kind}`}>{options.map(([value, label, note]) => <button type="button" className={selectedValue === value ? 'selected' : ''} key={value} onClick={async () => { const patch = kind === 'voice' && value !== 'off' && elevenVoices.length ? { tts_voice_id: value, voice: settings.voice === 'off' ? 'shimmer' : settings.voice, audio: true } : { [field]: value, ...(kind === 'voice' ? { tts_voice_id: null } : {}) }; await onSave(patch); onBack(); }}><span className="pickerMark">{selectedValue === value ? <Icon name="check" /> : kind === 'voice' ? <Icon name="volume" /> : null}</span><strong>{label}</strong>{note && <small>{note}</small>}</button>)}</div></section>;
}

function SettingsView({ settings, setSettings, navigate, onBack, openBotAction }: { settings: UserSettings; setSettings: (settings: UserSettings) => void; navigate: (view: ViewName) => void; onBack: () => void; openBotAction: (action: string) => void }) {
  async function save(patch: Partial<UserSettings>) { const data = await apiPost<any>('/api/miniapp/settings', patch); setSettings(data.settings); }
  const cards: Array<[ViewName | 'audio', IconName, string, string]> = [
    ['voice', 'volume', 'Voice', settings.audio ? settings.voice : 'Off'], ['speed', 'gauge', 'Speed', settings.speed.replace('_', ' ')],
    ['level', 'chart', 'Level', settings.level], ['audio', 'listen', 'Audio', settings.audio ? 'On' : 'Off'],
    ['goal', 'target', 'Goal', settings.goal], ['native', 'globe', 'Native', languageNames[settings.native_lang] || settings.native_lang],
    ['accent', 'message', 'Accent', settings.accent.toUpperCase()], ['reply', 'repeat', 'Reply', settings.voice_reply_mode === 'always' ? 'Always' : 'Voice only'],
  ];
  return <section className="screenPage settingsPage"><ScreenHeader title="Settings" onBack={onBack} /><div className="settingsGrid">{cards.map(([view, icon, label, value]) => <button type="button" key={label} onClick={() => view === 'audio' ? save({ audio: !settings.audio }) : navigate(view)}><span className="settingIcon"><Icon name={icon} /></span><span><strong>{label}</strong><small>{value}</small></span><Icon name="chevron" /></button>)}</div><h2 className="sectionLabel">ACCOUNT</h2><div className="accountList"><button type="button" onClick={() => openBotAction('subscription')}><span>Subscription &amp; plan</span><Icon name="chevron" /></button><button type="button" onClick={() => openBotAction('premium')}><span>Premium features</span><Icon name="chevron" /></button><button type="button" onClick={() => openBotAction('paysupport')}><span>Payment support</span><Icon name="chevron" /></button></div><button className="cancelButton" type="button" onClick={() => openBotAction('cancel-subscription')}>Cancel subscription</button></section>;
}

export default function App() {
  const tg = useMemo(() => getTelegramWebApp(), []);
  const [ready, setReady] = useState(false);
  const [home, setHome] = useState<HomeData | null>(null);
  const [buying, setBuying] = useState(false);
  const [openingAction, setOpeningAction] = useState<string | null>(null);
  const [view, setView] = useState<ViewName>(() => {
    if (!(import.meta as any).env?.DEV) return 'home';
    const requested = new URLSearchParams(window.location.search).get('view') as ViewName | null;
    return requested && ['home', 'vocabulary', 'quiz', 'studyPlan', 'settings', 'voice', 'speed', 'level', 'native', 'accent', 'goal', 'reply'].includes(requested) ? requested : 'home';
  });
  const [settings, setSettings] = useState<UserSettings>(settingDefaults);

  useEffect(() => {
    if (!tg) return;
    try {
      tg.ready();
      tg.expand();
      applyTelegramThemeCssVars(tg);
      setReady(true);
    } catch {
      setReady(false);
    }
  }, [tg]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!ready) return;
      try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const data = await apiGet<any>(`/api/miniapp/home?timezone=${encodeURIComponent(timeZone)}`);
        if (cancelled || !data?.ok) return;
        setHome({
          todayWords: Number(data.todayWords) || 0,
          unlearnedWords: Number(data.unlearnedWords) || 0,
          vocabCount: Number(data.vocabCount) || 0,
          dailyQuizDone: data.dailyQuizDone == null ? null : String(data.dailyQuizDone),
          mistakesCount: Number(data.mistakesCount) || 0,
          learnedWords: Number(data.learnedWords) || 0,
          dueWords: Number(data.dueWords) || 0,
          timeZone: String(data.timeZone || timeZone),
          activity: {
            currentStreak: Number(data.activity?.currentStreak) || 0,
            days: Array.isArray(data.activity?.days) && data.activity.days.length === 7 ? data.activity.days : localWeekDays(),
            todayActivity: data.activity?.todayActivity || emptyActivityCounts(),
            weekActivity: data.activity?.weekActivity || emptyActivityCounts(),
          },
          plan: data.plan === 'premium' || data.plan === 'complimentary' ? data.plan : 'free',
          limits: data.limits && typeof data.limits === 'object' ? data.limits : null,
          usage: data.usage && typeof data.usage === 'object' ? data.usage : null,
          premiumPriceStars: Number(data.premiumPriceStars) || null,
        });
      } catch {
        if (!cancelled) setHome(null);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    apiGet<any>('/api/miniapp/settings').then((data) => {
      if (data?.settings) setSettings({ ...settingDefaults, ...data.settings });
    }).catch(() => {});
  }, [ready]);

  const hasTelegramAuth = Boolean(tg?.initData);
  const devFullPreview = !hasTelegramAuth
    && Boolean((import.meta as any).env?.DEV)
    && new URLSearchParams(window.location.search).get('access') === 'full';
  const isFullAccess = devFullPreview || home?.plan === 'premium' || home?.plan === 'complimentary';
  const firstName = tg?.initDataUnsafe?.user?.first_name || tg?.initDataUnsafe?.user?.username || 'Denys';
  const unlearnedWords = home?.unlearnedWords ?? 0;
  const vocabCount = home?.vocabCount ?? 0;
  const dueWords = home?.dueWords ?? 0;
  const activityDays = home?.activity.days || localWeekDays();
  const todayActivities = home?.activity.todayActivity.total || 0;
  const weekActivities = home?.activity.weekActivity.total || 0;
  const currentStreak = home?.activity.currentStreak || 0;
  const vocabLimit = home?.limits?.vocab_words ?? 30;
  const configuredQuizLimit = home?.limits?.quizzes;
  const quizLimit = home && configuredQuizLimit === null
    ? null
    : configuredQuizLimit ?? (isFullAccess ? 5 : 1);
  const quizDone = home?.usage?.quizzes || 0;
  const weeklyTarget = 20;
  const weeklyProgress = Math.min(100, Math.max(0, (weekActivities / weeklyTarget) * 100));
  const actionBusy = Boolean(openingAction);
  const interfaceLocale = String(tg?.initDataUnsafe?.user?.language_code || navigator.language || 'en');

  async function buyPremium() {
    if (!hasTelegramAuth || !tg?.openInvoice || buying) return;
    setBuying(true);
    try {
      const result = await apiPost<{ ok: boolean; invoiceUrl?: string }>('/api/miniapp/subscription/invoice');
      if (!result?.invoiceUrl) throw new Error('Invoice was not created');
      tg.openInvoice(result.invoiceUrl, (status) => {
        setBuying(false);
        if (status === 'paid') window.location.reload();
        if (status === 'failed') tg.showAlert?.('Payment failed. Please try again.');
      });
    } catch {
      setBuying(false);
      tg.showAlert?.('Could not open the payment form. Please try again or use /premium in the bot chat.');
    }
  }

  async function openBotAction(action: string) {
    if (!tg || !hasTelegramAuth || actionBusy) return;
    setOpeningAction(action);
    try {
      await apiPost(`/api/miniapp/actions/${action}`);
      tg.close?.();
    } catch {
      setOpeningAction(null);
      tg.showAlert?.('Could not open this action. Please try again.');
    }
  }

  function actionDisabled(locked = false) {
    return (hasTelegramAuth && !ready) || actionBusy || locked;
  }

  const directScreen = view === 'vocabulary'
    ? <VocabularyView onBack={() => setView('home')} />
    : view === 'quiz'
      ? <QuizView onBack={() => setView('home')} tg={tg} />
      : view === 'studyPlan'
        ? <StudyPlanView onBack={() => setView('home')} />
      : view === 'settings'
        ? <SettingsView settings={settings} setSettings={setSettings} navigate={setView} onBack={() => setView('home')} openBotAction={openBotAction} />
        : view !== 'home'
          ? <PickerView kind={view} settings={settings} onSave={async (patch) => { const data = await apiPost<any>('/api/miniapp/settings', patch); setSettings(data.settings); }} onBack={() => setView('settings')} />
          : null;

  if (directScreen) {
    return <main className="appShell appShell--screen">{directScreen}<BottomNav view={view} onChange={setView} /></main>;
  }

  return (
    <main className={`appShell ${isFullAccess ? 'appShell--full' : 'appShell--limited'}`}>
      <div className="appPreviewBar">
        <span className="appPreviewLabel">Preview</span>
        <span className={`accessBadge ${isFullAccess ? 'accessBadge--full' : 'accessBadge--limited'}`}>
          {isFullAccess ? 'Full Access' : 'Limited Access'}
          <span className="accessBadgeDot" />
        </span>
      </div>

      <section className="heroCard">
        <div className="heroTop">
          <div>
            <p className="heroGreeting">Hello, {String(firstName)} 👋</p>
            <h1 className="heroTitle">Ready to learn?</h1>
          </div>
          <div className="heroPlanPill">
            <Icon name="flame" className="heroPlanIcon" />
            <span>{streakLabel(currentStreak)}</span>
          </div>
        </div>

        <div className="weekStrip" aria-label="Weekly activity">
          {activityDays.map((day) => {
            const label = new Intl.DateTimeFormat(interfaceLocale, { weekday: 'short', timeZone: 'UTC' })
              .format(new Date(`${day.date}T12:00:00.000Z`));
            const circleClass = [
              'weekDayCircle',
              day.active ? 'weekDayCircle--done' : '',
              day.isToday ? 'weekDayCircle--today' : '',
              day.isFuture ? 'weekDayCircle--future' : '',
            ].filter(Boolean).join(' ');
            return (
              <div className="weekDay" key={day.date}>
                <span className={circleClass}>{day.active ? '✓' : day.isToday ? '•' : ''}</span>
                <span className="weekDayLabel">{label}</span>
              </div>
            );
          })}
        </div>

        <div className="heroStats">
          <div className="heroStat"><span>Today</span><strong>{activityLabel(todayActivities)}</strong></div>
          <div className="heroStat"><span>Unlearned</span><strong>{pluralizeWords(unlearnedWords)}</strong></div>
          <div className="heroStat"><span>Access</span><strong>{isFullAccess ? 'Full ✦' : 'Free'}</strong></div>
        </div>
      </section>

      {!isFullAccess && (
        <button className="upgradeBanner" type="button" onClick={buyPremium} disabled={hasTelegramAuth && (!ready || buying || !tg?.openInvoice)}>
          <span className="upgradeIconBox"><Icon name="crown" /></span>
          <span className="upgradeCopy">
            <strong>{buying ? 'Opening payment…' : 'Unlock Full Access'}</strong>
            <small>Higher limits · More quizzes · Unlimited vocab</small>
          </span>
          <span className="roundArrow"><Icon name="arrow" /></span>
        </button>
      )}

      <button className="vocabModeCard" type="button" onClick={() => openBotAction('vocab-mode')} disabled={actionDisabled()}>
        <span className="vocabModeMain">
          <span className="emojiIconBox" aria-hidden="true">📚</span>
          <span className="vocabModeCopy">
            <strong>Start Vocabulary Mode</strong>
            <small>Learn {unlearnedWords} unlearned {unlearnedWords === 1 ? 'word' : 'words'}</small>
          </span>
          <span className="roundArrow"><Icon name="arrow" /></span>
        </span>
      </button>

      <section className="featureGrid">
        <button className="featureCard" type="button" onClick={() => openBotAction('dictionary')} disabled={actionDisabled()}>
          <span className="featureIcon featureIcon--blue"><Icon name="book" /></span>
          <strong>Dictionary</strong><small>Look up words</small>
        </button>
        <button className="featureCard" type="button" onClick={() => setView('vocabulary')} disabled={actionDisabled()}>
          <span className="featureIcon featureIcon--purple"><Icon name="star" /></span>
          <strong>My Vocabulary</strong><small>{isFullAccess ? pluralizeWords(vocabCount) : `${vocabCount} / ${vocabLimit} limit`}</small>
        </button>
        <button className="featureCard" type="button" onClick={() => setView('quiz')} disabled={actionDisabled()}>
          <span className="featureIcon featureIcon--green"><Icon name="brain" /></span>
          <strong>Daily Quiz</strong><small>{normalizeQuizStatus(home?.dailyQuizDone || null, quizDone, quizLimit)}</small>
        </button>
        <button className="featureCard" type="button" onClick={() => openBotAction('repeat')} disabled={actionDisabled()}>
          <span className="featureIcon featureIcon--red"><Icon name="alert" /></span>
          <strong>Review</strong><small>{dueWords} due</small>
        </button>
      </section>

      {!isFullAccess && (
        <div className="freeLimitNote">
          <Icon name="lock" />
          <span>You can save up to {vocabLimit} words on the free plan. <button type="button" onClick={buyPremium}>Upgrade</button> for unlimited.</span>
        </div>
      )}

      <button className="studyPlanCard" type="button" onClick={() => setView('studyPlan')} disabled={actionDisabled()}>
        <span className="studyPlanCardIcon"><Icon name="target" /></span>
        <span className="studyPlanCardCopy"><strong>Your Study Plan</strong><small>17 topics · Personalized priorities</small></span>
        <Icon name="arrow" className="progressArrow" />
      </button>

      <button className={`progressCard ${!isFullAccess ? 'progressCard--limited' : ''}`} type="button" onClick={() => openBotAction('progress')} disabled={actionDisabled()}>
        <span className="progressMain">
          <span className="progressIcon"><Icon name="chart" /></span>
          <span className="progressCopy"><strong>Your Progress</strong><small>Stats, streak &amp; achievements</small></span>
          <Icon name="arrow" className="progressArrow" />
        </span>
        <span className="weeklyGoal">
          <span className="limitLabels"><span>Weekly goal</span><strong>{weekActivities} / {weeklyTarget} activities</strong></span>
          <span className="weeklyTrack"><span className="weeklyValue" style={{ width: `${weeklyProgress}%` }} /></span>
        </span>
      </button>

      <section className="quickActions">
        <h2>Quick Actions</h2>
        <div className="quickActionRow">
          <button className="quickButton quickButton--blue" type="button" onClick={() => openBotAction('add-word')} disabled={actionDisabled()}><Icon name="add" /><span>Add Word</span></button>
          <button className="quickButton quickButton--green" type="button" onClick={() => openBotAction('repeat')} disabled={actionDisabled()}><Icon name="repeat" /><span>Review</span></button>
          <button className="quickButton quickButton--purple" type="button" onClick={() => openBotAction('listen-test')} disabled={actionDisabled()}><Icon name="listen" /><span>Listen</span></button>
        </div>
      </section>
      <BottomNav view="home" onChange={setView} />
    </main>
  );
}
